import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import { InfiniteQuery } from 'types/app';
import { AnalyticsCompany, OrganicInsightsSortFields, SortDirections } from 'api';
import { DataTable, DataTableColumn } from 'components';
import { SortAction, SortParam } from 'components/DataTable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface Props extends InfiniteQuery<AnalyticsCompany> {
  sortBy: SortParam;
  onSort: SortAction;
}

const CompaniesTable: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  sortBy,
  onSort,
}) => {
  const { t } = useTranslation();

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const columns: DataTableColumn<AnalyticsCompany>[] = [
    {
      width: 80,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: ({ company }) => (
          <Avatar
            src={company.company_logo}
            variant="square"
            style={{ height: 56, width: 56, borderRadius: 4 }}
          />
        ),
      },
    },
    {
      width: 300,
      headerCell: {
        render: t('common:company'),
      },
      bodyCell: {
        render: company => <Typography variant="h6">{company.company.job_company_name}</Typography>,
      },
    },
    {
      width: 150,
      headerCell: {
        sort: {
          field: OrganicInsightsSortFields.TotalFollowers,
          direction: SortDirections.Desc,
        },
        render: t('insights:number_engagers'),
      },
      bodyCell: {
        render: company => (
          <Typography variant="subtitle2" align="center">
            {company.total_engagers}
          </Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:change'),
      },
      bodyCell: {
        render: getChange,
      },
    },
    {
      width: 300,
      headerCell: {
        render: t('insights:engager_roles'),
      },
      bodyCell: {
        render: company => {
          const roles = company.engager_roles.map(follower => follower.role.split('_').join(' '));
          const firstFive = roles.slice(0, 5);
          const rest = roles.slice(5).length;

          return (
            <Tooltip title={roles.join('\n')}>
              <Typography>
                {rest ? `${firstFive.join(', ')} +${rest}` : `${firstFive.join(', ')}`}
              </Typography>
            </Tooltip>
          );
        },
      },
    },
    {
      width: 300,
      headerCell: {
        render: t('insights:engager_title'),
      },
      bodyCell: {
        render: company => {
          const titles = company.engager_titles.map(follower => follower.title);
          const firstFiveTitles = titles.slice(0, 5);
          const rest = titles.slice(5).length;

          return (
            <Tooltip title={titles.join('\n')}>
              <Typography>
                {rest ? `${firstFiveTitles.join(', ')} +${rest}` : `${firstFiveTitles.join(', ')}`}
              </Typography>
            </Tooltip>
          );
        },
      },
    },
  ];

  function getChange(company: AnalyticsCompany) {
    const change = Number(company.change);
    if (change === 0) {
      return '-';
    }

    return (
      <Typography
        variant="subtitle2"
        align="center"
        component="span"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {change > 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        {Math.abs(change)}
      </Typography>
    );
  }

  return (
    <DataTable
      pagination={pagination}
      // @ts-ignore
      data={results}
      columns={columns}
      actions={() => null}
      rowSelection={null}
      onSort={onSort}
      sortBy={sortBy}
    />
  );
};

export default CompaniesTable;
